import { action, observable, computed } from 'mobx'
import { OrganizationalStructureRepository } from './OrganizationalStructureRepository'
import { ControllerStoreBase } from 'src/controllers/ControllerStoreBase'
import { GlobalController } from 'src/shared/global/GlobalController'
import { groupBy, findKey } from 'lodash'

export class OrganizationalStructureController extends ControllerStoreBase {
  @observable organizationalStructureOptions = {}
  @observable selectedValues = {}
  @observable activeOrganizationalStructure
  @observable selectedOrganizationalStructure

  __levelNameMap = {}
  globalController = GlobalController.getInstance()

  static getInstance () {
    return super.construct(new OrganizationalStructureController(), true)
  }

  __toLowerCaseAndAccentRemoval = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  }

  @action loadOrganizationalStructureComponent = async () => {
    const profile = await OrganizationalStructureRepository.findProfile()
    const response = await OrganizationalStructureRepository.findOrganizationalStructures()
    this.organizationalStructureOptions = groupBy(
      response,
      ({ levelName, level }) => {
        const normalizedLevelName = this.__toLowerCaseAndAccentRemoval(levelName)
        this.__levelNameMap[level] = { key: normalizedLevelName, levelName }
        return normalizedLevelName
      }
    )
    Object.keys(this.__levelNameMap).forEach(level => {
      const levelChildIsClerable = response.filter(og => og.level == level).some(og => og.clerable)
      const key = `${+level + 1}`
      if (this.__levelNameMap[key]) {
        this.__levelNameMap[key] = { ...this.__levelNameMap[key], clerable: levelChildIsClerable }
      }
    })
    this.activeOrganizationalStructure = response?.find((element) => {
      return element.id == profile.activeOrganizationalStructure
    })
    this.selectedOrganizationalStructure = this.activeOrganizationalStructure
    this.globalController.setProfile(profile)
    this.__structureSetup(profile, response)
  }

  @action onOrganizationalStructureLevelSelect = (option) => {
    if (option) {
      this.selectedOrganizationalStructure = option
      this.__organizationalStructureLevelDependencyHandler(option)
    }
  }

  @action onClearOrganizationalStructureLevelSelect = (option) => {
    if (option) {
      const parentLevelName = this.getParentKey(option)
      const parentOptions = this.organizationalStructureOptions[parentLevelName]
      this.selectedOrganizationalStructure = {
        ...parentOptions.find((osOpt) => osOpt.id === option.parent)
      }
      this.__organizationalStructureLevelDependencyHandler(option, true)
    }
  }

  @action onSave () {
    try {
      this.activeOrganizationalStructure = this.selectedOrganizationalStructure
      OrganizationalStructureRepository.activateOrganizationalStructureById(
        this.activeOrganizationalStructure.id
      )
      window.dispatchEvent(new CustomEvent('organizational-structure-changed', {
        detail: {
          id: this.activeOrganizationalStructure.id,
          timestamp: new Date().toISOString()
        }
      }))
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  __structureSetup = (profile, organizationalStructureList) => {
    const currentStructure = organizationalStructureList.find(
      (os) => os.id === (profile.activeOrganizationalStructure || profile.id)
    )
    const levelName = this.__levelNameMap[currentStructure.level].key
    this.__structureSetupSelectedValues(levelName, currentStructure)
    if (currentStructure.parent) {
      this.__structureSetup(
        { id: currentStructure.parent },
        organizationalStructureList
      )
    }
  }

  __structureSetupSelectedValues = (levelName, payload) => {
    this.selectedValues = {
      ...this.selectedValues,
      [levelName]: payload
    }
  }

  __organizationalStructureLevelDependencyHandler = (option, clean = false) => {
    const dependent = this.getChildrenKey(option)
    setTimeout(() => {
      const filteredByParent = this.__getSelectedOrganizationalDependentsByParent(option, dependent)
      if (filteredByParent?.length > 1 || clean) {
        this.__cleanDependent(dependent)
      } else if (filteredByParent?.length === 1) {
        this.__structureSetupSelectedValues(dependent, filteredByParent[0])
        this.onOrganizationalStructureLevelSelect(filteredByParent[0])
      }
    }, 50)
  }

  __getSelectedOrganizationalDependentsByParent = (selected, dependent) => {
    return this.organizationalStructureOptions[dependent]?.filter(
      (dependentOptions) => dependentOptions.parent === selected.id
    )
  }

  __cleanDependent = (dependent) => {
    const selectedValuesCopy = {
      ...this.selectedValues[dependent],
      id: null
    }
    this.selectedValues[dependent] = null
    this.__organizationalStructureLevelDependencyHandler(selectedValuesCopy)
  }

  @action getChildrenKey = (option) => {
    if (!option) return
    return this.__levelNameMap[option.level + 1]?.key
  }

  @action getParentKey = (option) => {
    return this.__levelNameMap[option.level - 1]?.key
  }

  @action getParent = (option) => {
    return this.__levelNameMap[option.level + 1]
  }

  @action getChildrens = (option) => {
    return this.__levelNameMap[option.level - 1]
  }

  getName () {
    return 'OrganizationalStructureController'
  }
}
